import { useEffect, useMemo, useState } from "react";
import { Tooltip, Typography } from "@mui/material";
import useUserData from "./../../hooks/useUserData.js";
import { routeAllowed } from "../../pages";
import { useTheme } from "styled-components";
import {
  SidebarContainer,
  SidebarHeader,
  MenuWrapper,
  MenuItem,
  SidebarHeaderBox,
  MainHeader,
  MenuLink,
  MenuIcon,
  FooterContainer,
  ProfileWrapper,
  Circle,
  TextBox,
  ProfileLink,
  RightIcon,
  Logo,
  StyledMenu,
  StyledMenuItem,
  StyleParentIcon,
  NoWrapTypography,
  navTooltipSlotProps,
} from "./style.js";
import useLanguage from "../../hooks/useLanguage.js";
import useMediaQuery from "../../hooks/useMediaQuery.js";

export default function Sidebar({
  routes,
  specialRoutes,
  setComponent,
  setTheme,
  onLogout,
  onOpenProfile,
}) {
  const [active, setActive] = useState(window.location.pathname);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isExtended, setIsExtended] = useState(
    !!+window.localStorage.getItem("isExtended")
  );
  const userData = useUserData();
  const isMobile = useMediaQuery();
  const theme = useTheme()?.palette?.mode;
  const [language, setLanguage] = useLanguage();
  const pt = language?.layout?.sidebar?.profile;
  // MUI's Popper only flips top/bottom-start/end for RTL, never left/right.
  const tipSide = language?.direction === "rtl" ? "left" : "right";

  const visibleRoutes = useMemo(
    () =>
      routes.filter((r) => routeAllowed(r, userData?.role)),
    [routes, userData?.role]
  );

  const handleClick = (route) => {
    window.history.pushState({}, "", route);
    const markedRoute =
      routes.find(
        (r) =>
          r.route === route || (r.route !== "/" && route.startsWith(r.route))
      )?.parent || route;
    setActive(markedRoute);
    setComponent(
      specialRoutes[route] ||
        routes.find(
          (r) =>
            r.route === route || (r.route !== "/" && route.startsWith(r.route))
        ) ||
        specialRoutes[404]
    );
  };

  const handleToggle = () => {
    setIsExtended(!isExtended);
    window.localStorage.setItem("isExtended", +!isExtended);
  };

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
  };

  const handleChangeTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const handleChangeLanguage = () => {
    setLanguage(language.code === "en" ? "heb" : "en");
    window.location.reload();
  };

  useEffect(() => {
    if (isMobile) setIsExtended(false);
  }, [isMobile]);

  // Mount only — resolves the URL the user landed on into a component. Adding
  // the deps eslint wants would re-run it on every navigation and pushState in
  // a loop (handleClick is a new function each render).
  useEffect(() => {
    handleClick(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SidebarContainer className={isExtended ? "extended" : "minimized"}>
      <SidebarHeader>
        <SidebarHeaderBox>
          <Logo src="/assets/logo.svg" alt="logo" />
          {isExtended && (
            <MainHeader variant="h2">
              {language?.layout?.sidebar.siteName}
            </MainHeader>
          )}
        </SidebarHeaderBox>
        {!isMobile && (
          <RightIcon
            className={
              "circled" +
              (isExtended ? "" : " minimized") +
              " " +
              language.direction
            }
            src="/assets/arrow.svg"
            alt="chevron-down"
            onClick={handleToggle}
          />
        )}
      </SidebarHeader>
      <MenuWrapper>
        {visibleRoutes.map(
          ({ name, route, icon, parent }) =>
            !parent && (
              <MenuItem key={name} className={active === route && "active"}>
                <Tooltip
                  title={isExtended ? "" : language?.pages?.[name]?.title}
                  placement={tipSide}
                  slotProps={navTooltipSlotProps}
                >
                  <MenuLink onClick={() => handleClick(route)} id={route}>
                    {parent && <StyleParentIcon />}
                    {icon && (
                      <MenuIcon src={`/assets/icons/${icon}.svg`} alt={name} />
                    )}
                    {isExtended && (
                      <Typography>{language?.pages?.[name]?.title}</Typography>
                    )}
                  </MenuLink>
                </Tooltip>
              </MenuItem>
            )
        )}
      </MenuWrapper>
      <MenuItem style={{ marginTop: "auto" }} />
      <FooterContainer>
        <ProfileWrapper>
          <Tooltip
            title={isExtended ? "" : userData?.nickname || userData?.email || ""}
            placement={tipSide}
            slotProps={navTooltipSlotProps}
          >
            <ProfileLink
              className={!isExtended && "minimized"}
              onClick={handleProfileClick}
            >
              <Circle $background={userData?.color}>{userData?.initials}</Circle>
              {isExtended && (
                <TextBox>
                  <Tooltip title={userData?.email}>
                    <NoWrapTypography>{userData?.nickname}</NoWrapTypography>
                  </Tooltip>
                  <Tooltip title={userData?.email}>
                    <NoWrapTypography variant="caption">
                      {userData?.email}
                    </NoWrapTypography>
                  </Tooltip>
                </TextBox>
              )}
              {isExtended && (
                <RightIcon src="/assets/options.svg" alt="chevron-down" />
              )}
            </ProfileLink>
          </Tooltip>
          <StyledMenu
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleProfileClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <StyledMenuItem onClick={handleChangeTheme}>{pt[0]}</StyledMenuItem>
            <StyledMenuItem onClick={handleChangeLanguage}>
              {pt[1]}
            </StyledMenuItem>
            <StyledMenuItem
              onClick={() => {
                handleProfileClose();
                onOpenProfile?.();
              }}
            >
              {pt[2]}
            </StyledMenuItem>
            <StyledMenuItem onClick={onLogout}>{pt[3]}</StyledMenuItem>
          </StyledMenu>
        </ProfileWrapper>
      </FooterContainer>
    </SidebarContainer>
  );
}
